/**
 * The merged background-run feed: one row shape for work the CONNECTOR did and work BILLING
 * did, and the wording that keeps the two apart on screen.
 *
 * WHY THIS EXISTS. A brand's background work comes from two systems that record it in two
 * places, and the console read one of them. So a brand page could show the inventory sync and
 * never the invoice build that followed it — and, worse, the half it did show was labelled
 * "Sync History", which reads as "everything that ran". An admin asking "did anything run for
 * this brand last night" was answered by half the record with no sign that it was half.
 *
 * `GET /connector/brands/:id/runs` returns both halves in one shape. Every row carries
 * `source`, and every table built on it MUST show that column: two systems with different
 * schedules, different failure modes and different retry limits, rendered in one list with no
 * column saying which is which, is a list that invites the wrong conclusion about both.
 *
 * The wording lives here rather than in the two components because the judgements are the
 * same judgements — what "Completed" does and does not promise, what a missing count means —
 * and two copies is how one screen comes to answer that differently from the other.
 */

/** The Source column's values. The raw enum is lowercase; these are what a reader sees. */
export const SOURCE_LABELS = {
  connector: 'Connector',
  billing: 'Billing',
  // Reads of the brand's OWN Shopify store, which is neither of the other two. They used to
  // render as Connector — not by decision, but because a connector-queue row carries no
  // source and the fallback below prints that. The connector moves data to and from
  // RETAILERS, which these never touch, and one of them exists only to feed a billing
  // setting, so Connector answered "who do I call when this fails" with the wrong team.
  shopify: 'Shopify',
};

export const sourceLabel = (source) => SOURCE_LABELS[source]
  // Not "Unknown". A source this file has not been taught is still a real system that ran the
  // job, and printing the raw value is how someone finds out which one.
  || (source ? String(source) : 'Connector');

/**
 * Why the Source column is worth a hover: the two systems are not two views of one pipeline.
 */
export const SOURCE_TIPS = {
  connector: 'Run by the connector — the work that moves orders, SKUs, stock and tracking '
    + 'between 2W and the retailers.',
  billing: 'Run by billing — the work that turns orders already in 2W into commission and '
    + 'invoices. It never talks to a retailer, so a retailer outage cannot fail it and a '
    + 'billing failure never means an order was missed.',
  shopify: 'A read of the brand’s own Shopify store — its catalogue, and the locations and '
    + 'warehouses that decide which orders 2W charges shipping on. It talks to Shopify, not '
    + 'to a retailer, so it fails when Shopify does; but what it feeds can change an invoice.',
};

export const sourceTip = (source) => SOURCE_TIPS[source]
  || `Recorded by "${source}", which this screen has no description for.`;

export const SOURCE_COLUMN_TIP = 'Which system ran the job. The connector moves data between '
  + '2W and the retailers; Shopify reads the brand’s own store; billing turns orders '
  + 'already in 2W into commission and invoices. They run on separate schedules and fail for '
  + 'separate reasons, so a row from one says nothing about the health of the others.';

/**
 * The response may be the array itself or an envelope around it.
 *
 * Written before the route shipped, deliberately tolerant of both: a table that renders
 * nothing because the rows arrived one key deeper is indistinguishable, on screen, from a
 * brand that has never run anything.
 */
export const runsFrom = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.runs)) return payload.runs;
  if (Array.isArray(payload?.jobs)) return payload.jobs;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

/** Local date and time, or null — so a caller can leave the sentence out rather than print "null". */
const at = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
};

/**
 * "attempt 2 of 5", or "attempt 2" when the limit was not recorded.
 *
 * Returns null when there is no attempt count at all, so the caller drops the sentence instead
 * of writing "attempt null of null" — which is what dropping `attempts` from the row shape was
 * supposed to avoid in the first place.
 */
export const attemptPhrase = (row) => {
  const made = Number(row?.attempts);
  if (!Number.isFinite(made) || made <= 0) return null;
  const max = Number(row?.maxAttempts);
  return Number.isFinite(max) && max > 0 ? `attempt ${made} of ${max}` : `attempt ${made}`;
};

/**
 * The badge a run earns — its WORDS as well as its colour, because there are three outcomes
 * and the status column only ever held two.
 *
 * A run that got some of its work through and lost the rest is recorded COMPLETED: it
 * finished, and the rows it wrote are real. On the status alone that is a green badge, which
 * hides the loss behind the one word people read as "everything worked" — and FAILED would be
 * the opposite lie, denying work that did land and sending someone looking for an outage. So
 * the flag beside the status is what the badge reads: "Completed with errors", amber.
 *
 * Red stays reserved for FAILED. A screen where two different things are red is a screen where
 * neither one is urgent, and the distinction being drawn here is exactly "this run did some of
 * its job" against "this run did none of it".
 *
 * Keyed on COMPLETED and not on the flag alone: `partial` is only ever written on a finished
 * run, and a RUNNING row that somehow carried it must not be announced as completed.
 *
 * Here rather than in the table for the reason at the top of this file: the row and the detail
 * dialog behind its eye both show this badge, and two copies of the rule is how the same run
 * comes to read "Completed with errors" in the list and plain green one click in.
 */
export const statusBadge = (row) => {
  const status = row?.status;
  if (status === 'COMPLETED' && row?.partial) {
    return { text: 'Completed with errors', tone: 'warning' };
  }
  if (status === 'COMPLETED') return { text: status, tone: 'success' };
  if (status === 'FAILED') return { text: status, tone: 'critical' };
  return { text: status, tone: 'attention' };
};

/**
 * What each status means FOR THIS ROW, as one hover.
 *
 * The important one is COMPLETED. It is the status people read as "everything worked", and it
 * is not: the run finished, and an order or SKU the retailer refused is recorded against that
 * order or SKU rather than failing the run around it. A green badge with no explanation is how
 * a brand went a week believing every SKU had reached a retailer that had refused half of them.
 *
 * FAILED carries the FULL error, because the Error column truncates and a truncated stack
 * trace is not an error message. RETRYING carries the attempt count and the last attempt time,
 * because "it failed" and "it failed twice and is still going" are different situations.
 *
 * A COMPLETED row carrying `partial` gets its own sentence instead, because it is a different
 * event: the run lost some of what it was given and saved the rest. Its badge reads "Completed
 * with errors", and a badge that says "errors" has to be able to say which errors and what
 * they cost — the loss itself arrives as the row's note, appended below.
 */
export const statusTip = (row) => {
  const bits = [];
  const attempt = attemptPhrase(row);
  const started = at(row?.startedAt);
  const finished = at(row?.completedAt);

  switch (row?.status) {
    case 'COMPLETED':
      // Partial is checked first, and only inside COMPLETED, because that is the only status
      // the flag is ever written on: the run finished, so it is completed, and the flag is the
      // part the status cannot carry. The ordinary sentence below would be actively wrong
      // here — it says the run's own work went through and only a retailer's refusals did not.
      bits.push(row?.partial
        ? 'The run finished and some of what it was given did not get through. What it wrote '
          + 'is real — those rows are in 2W — but the figures beside it are short by whatever '
          + 'was lost, and what is missing stays missing until it runs again. Not a failure '
          + 'and not a clean run, which is why it is neither green nor red.'
        : 'The run finished. That is not a promise that everything in it succeeded — an order '
          + 'or SKU the retailer refused is recorded against that order or SKU, and the run '
          + 'still completes.');
      if (finished) bits.push(`Finished ${finished}.`);
      break;
    case 'FAILED':
      bits.push('The run stopped before finishing, so treat its figures as partial.');
      bits.push(row?.error
        ? `Error: ${row.error}`
        : 'No error message was recorded, which usually means the process was killed rather '
          + 'than throwing.');
      break;
    case 'RUNNING':
      bits.push(started
        ? `Still running, started ${started}.`
        : 'Still running; no start time was recorded.');
      bits.push('Its figures are not final until it ends.');
      break;
    case 'PENDING':
      bits.push('Queued and not started yet. Nothing has been attempted, so a blank count '
        + 'here means "not yet", not "nothing to do".');
      break;
    case 'RETRYING':
      bits.push(attempt
        ? `Failed and is being retried — ${attempt}.`
        : 'Failed and is being retried.');
      if (started) bits.push(`The last attempt started ${started}.`);
      if (row?.error) bits.push(`Last error: ${row.error}`);
      break;
    case 'CANCELLED':
      bits.push('Stopped before it finished — by a person, or by a shutdown. It did not fail: '
        + 'nothing is retried and no error is recorded.');
      break;
    default:
      bits.push(`Reported as "${row?.status ?? 'unknown'}", which this screen has no `
        + 'description for. The row is shown as it was recorded rather than being hidden.');
      break;
  }

  // Attempts are surfaced on EVERY status that has them, not only on a retry. A row that
  // completed on its third attempt looks identical to one that completed first time, and the
  // difference is the whole story of a flaky retailer.
  if (attempt && row?.status !== 'RETRYING') bits.push(`Ran as ${attempt}.`);
  if (row?.note) bits.push(`Note: ${row.note}`);
  return bits.join(' ');
};

/**
 * What a run's one figure counts, per unit.
 *
 * Only the connector's units are named here. Billing reports its own, and an unknown unit gets
 * a sentence that still says which unit it is — better than a confident wrong gloss, and it
 * means a new job type on either side is readable the day it ships rather than the day this
 * map is updated.
 */
export const UNIT_MEANINGS = {
  orders: 'Orders the run got through.',
  SKUs: 'SKUs the run got through.',
  // Not "SKUs sent", and now used by TWO run types: the stock comparison, and the per-brand
  // run behind a Shopify stock change. Neither sends anything — both put SKUs in the outbox
  // for the inventory push to send, and only while that push is switched on. Worded for both,
  // because a unit that names one of them reads as a wrong label on the other.
  queued: 'Nothing was sent to a retailer by this run. It put these SKUs in the queue for the '
    + 'inventory push to send, which happens on its own cadence and only while that push is '
    + 'switched on — so this counts SKUs queued, not SKUs the retailer has.',
  pushed: 'Tracking numbers sent to the retailer.',
};

/**
 * The Processed hover, including the case where there is no figure.
 *
 * A null count must SAY it is a null count. Printing 0 reads as "ran and did nothing", which
 * is a different and much more alarming statement than "this job type keeps no count" — and
 * the two were indistinguishable in this column for as long as it existed.
 */
export const processedTip = (processed) => {
  if (!processed || processed.value === null || processed.value === undefined) {
    return 'This job type records no count of its own, so there is nothing to show here. '
      + 'Blank rather than 0: 0 would say the run got through nothing, which is a different '
      + 'claim. What it did is in the Status and Error columns.';
  }
  const unit = processed.unit || 'items';
  const meaning = UNIT_MEANINGS[unit] || `"${unit}" is the unit this job type reports.`;
  return `${processed.value} ${unit}. ${meaning} Each job type reports a different unit, which `
    + 'is why this column is labelled per row rather than in the heading.';
};

/**
 * What each kind of run actually DOES, keyed by the ledger's job type.
 *
 * This is the one thing a detail dialog can say that the row cannot. Everything else on the
 * row — the type, the system, the count — was being repeated in the dialog, which made
 * opening it a waste of a click. The question it leaves unanswered is what the job is doing
 * to the data, and that is what this answers.
 *
 * Present tense, concrete, and naming which side it writes to. An admin deciding whether a
 * stuck run matters needs to know it writes to a retailer, not what it is called.
 *
 * Keyed by MiraklSyncJobType / SyncJobType. QueueTable keeps its own copy keyed by the
 * QUEUE's job names ('sku-map-refresh' rather than 'SKU_MAP_REFRESH') because the two name
 * spaces are genuinely different — the same wording, reachable from either key.
 */
export const RUN_EXPLAIN = {
  // Connector
  ORDER_SYNC: 'Collects new orders from the retailer and stores them in 2W. It reads only — nothing is sent to the retailer by this run.',
  SKU_MAP_REFRESH: 'Reads everything the retailer lists, so 2W knows which of their SKUs belong to which brand. It reads only.',
  INVENTORY_SYNC: 'Sends stock figures to the retailer, or queues them to be sent — the count says which. Queued figures go out on the inventory push, and only while that push is switched on.',
  INVENTORY_RECONCILE: 'Compares the stock 2W holds against what the retailer last reported and queues a push for anything that differs. It sends nothing itself.',
  TRACKING_PUSH: 'Tells the retailer an order has shipped, with its tracking number. This writes to the retailer.',
  CATALOGUE_SYNC: "Reads the brand's Shopify catalogue into 2W, so SKUs and stock can be compared against what the retailer holds. It reads only.",
  LEDGER_PRUNE: 'Housekeeping: removes old run records so this history stays readable. It touches no retailer and no brand.',
  MARKETPLACE_VERIFY: 'A retired check that is no longer part of 2W. Rows of this type are from before it was removed.',
  // Billing
  SCHEDULED_SYNC: 'Pulls this brand’s Shopify orders into billing on its schedule, so commission can be worked out from them.',
  HISTORICAL_SYNC: 'Pulls a past period’s Shopify orders into billing, to fill a gap or correct what was already there.',
  MANUAL_SYNC: 'The same pull as the scheduled one, started by a person rather than by the schedule.',
  INITIAL_SYNC: 'The first pull of a brand’s Shopify orders into billing, run once when the brand is set up.',
  ORDER_SYNC_BILLING: 'Pulls Shopify orders into billing so commission can be worked out from them.',
  REFUND_SYNC: 'Pulls Shopify refunds into billing, so commission already charged can be credited back.',
  REPORT_GENERATION: 'Builds the figures behind an invoice or a report from orders already in 2W. It reads only.',
};

/** The explanation for a run, or null when its type has none. */
export const runExplain = (run) => {
  if (!run?.type) return null;
  // Billing's ORDER_SYNC means something different from the connector's, and the two share a
  // name. The source is what tells them apart, and getting it wrong would describe an order
  // fetch from a retailer as a billing pull.
  if (run.source === 'billing' && run.type === 'ORDER_SYNC') return RUN_EXPLAIN.ORDER_SYNC_BILLING;
  return RUN_EXPLAIN[run.type] || null;
};
